/**
 * QuoteDialog — create / edit an "Extra / Quote" from a takeoff label.
 *
 * The quote is a priced SNAPSHOT of one label's scope (lines, section
 * breakdown, subtotal) plus a stored adjustment (add/deduct % + reason).
 * Later takeoff/rate edits never move a saved quote — "Refresh from current
 * takeoff" is the explicit revision path and shows before/after first.
 *
 * Rounding rule (Oct-9 list #3): the subtotal is rounded to cents once;
 * the adjustment is rounded to cents once (half-up) and added/subtracted
 * from the cent subtotal — so 2551.87 − 35% shows −893.15 and 1658.72,
 * and +10% shows +255.19 and 2807.06. The saved % is never re-applied to
 * an already-adjusted number.
 */
import { useEffect, useMemo, useState } from "react";
import { Printer, RefreshCw } from "lucide-react";
import { Overlay, DialogTitle } from "./dialogs";
import { BTN_GHOST, BTN_ORANGE, INPUT, LABEL, MONO } from "./theme";
import { money, fmtQty } from "./pricing";
import { uid } from "./geometry";

export const QUOTE_SECTIONS = ["Pipe Insulation", "Duct Insulation", "Rigid Supports"];

/** Cent rounding, half-up — the one place quote money is rounded. */
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const quoteTotals = (subtotal, direction, pct) => {
  const sub = round2(subtotal);
  const p = Math.max(0, Number(pct) || 0);
  if (direction !== "add" && direction !== "deduct" || p === 0) {
    return { subtotal: sub, adjustmentAmount: 0, total: sub };
  }
  const adj = round2(sub * (p / 100));
  return {
    subtotal: sub,
    adjustmentAmount: direction === "deduct" ? -adj : adj,
    total: round2(direction === "deduct" ? sub - adj : sub + adj),
  };
};

const snapLines = (est) => (est.lines || []).map((l) => ({
  section: l.section, description: l.description, labels: l.labels || [],
  quantity: l.quantity, unit: l.unit, rate: l.rate, color: l.color, source: l.source || null,
}));

export function quoteCsv(q) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const adj = q.adjustment || {};
  const head = [
    ["Extra / Quote", q.name],
    ["Source label", q.label],
    ["Date", new Date(q.updatedAt || q.createdAt || Date.now()).toLocaleDateString()],
    ["Subtotal", q.subtotal.toFixed(2)],
    ["Adjustment", adj.direction && adj.direction !== "none" ? `${adj.direction === "deduct" ? "Deduct" : "Add"} ${adj.pct}%${adj.reason ? ` — ${adj.reason}` : ""}` : "None", q.adjustmentAmount ? q.adjustmentAmount.toFixed(2) : "0.00"],
    ["Total", q.total.toFixed(2)],
    [],
    ["Section", "Description", "Labels", "Qty", "Unit", "Unit price", "Line total"],
  ];
  const rows = (q.lines || []).map((l) => [
    l.section, l.description, (l.labels || []).join(", "), l.quantity, l.unit, l.rate, round2(l.quantity * l.rate).toFixed(2),
  ]);
  return [...head, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
}

export const downloadQuoteCsv = (q) => {
  const blob = new Blob([quoteCsv(q)], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `Quote-${(q.name || q.label || "extra").replace(/[^\w-]+/g, "_")}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const printQuote = (q) => {
  const adj = q.adjustment || {};
  const rows = QUOTE_SECTIONS.map((sec) => {
    const lines = (q.lines || []).filter((l) => l.section === sec);
    if (!lines.length) return "";
    const sub = round2(lines.reduce((s, l) => s + l.quantity * l.rate, 0));
    return `<tr><td colspan="5" style="padding:8px 6px 2px;font-weight:700;text-transform:uppercase;font-size:10px;letter-spacing:.08em;color:#64748b">${sec}</td>
      <td style="text-align:right;font-weight:700;padding:8px 6px 2px">${money(sub)}</td></tr>` +
      lines.map((l) => `<tr>
        <td style="padding:4px 6px;border-bottom:1px solid #e2e8f0">${l.description}${l.source === "Automatic" ? ' <span style="font-size:9px;color:#2563eb">AUTO</span>' : ""}</td>
        <td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;color:#64748b">${(l.labels || []).join(", ")}</td>
        <td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:right">${fmtQty(l.quantity)}</td>
        <td style="padding:4px 6px;border-bottom:1px solid #e2e8f0">${l.unit}</td>
        <td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:right">${money(l.rate)}</td>
        <td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:right;font-weight:600">${money(round2(l.quantity * l.rate))}</td>
      </tr>`).join("");
  }).join("");
  const w = window.open("", "_blank", "width=800,height=900");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>${q.name}</title>
    <style>body{font-family:ui-sans-serif,system-ui,sans-serif;color:#0f172a;padding:32px}table{width:100%;border-collapse:collapse;font-size:12px}</style></head><body>
    <h1 style="font-size:20px;margin:0">${q.name}</h1>
    <p style="color:#64748b;font-size:12px;margin:4px 0 16px">Extra / quote from label <b>${q.label}</b> · ${new Date(q.updatedAt || q.createdAt || Date.now()).toLocaleDateString()}</p>
    <table><thead><tr style="text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:#64748b">
      <th style="padding:4px 6px">Description</th><th style="padding:4px 6px">Labels</th><th style="padding:4px 6px;text-align:right">Qty</th>
      <th style="padding:4px 6px">Unit</th><th style="padding:4px 6px;text-align:right">Unit price</th><th style="padding:4px 6px;text-align:right">Total</th>
    </tr></thead><tbody>${rows}</tbody></table>
    <div style="margin-top:16px;margin-left:auto;width:280px;font-size:13px">
      <div style="display:flex;justify-content:space-between;padding:3px 0"><span>Subtotal</span><b>${money(q.subtotal)}</b></div>
      ${q.adjustmentAmount ? `<div style="display:flex;justify-content:space-between;padding:3px 0;color:${q.adjustmentAmount < 0 ? "#b91c1c" : "#047857"}"><span>${adj.direction === "deduct" ? "Deduct" : "Add"} ${adj.pct}%${adj.reason ? ` — ${adj.reason}` : ""}</span><b>${money(q.adjustmentAmount)}</b></div>` : ""}
      <div style="display:flex;justify-content:space-between;padding:6px 0;border-top:2px solid #0f172a;font-size:16px"><span><b>Total</b></span><b>${money(q.total)}</b></div>
    </div>
    <script>window.print()</script></body></html>`);
  w.document.close();
};

export default function QuoteDialog({ open, onClose, label, freshEst, quote, computeLive, onSave }) {
  const editing = !!quote;
  const [name, setName] = useState("");
  const [direction, setDirection] = useState("none"); // none | add | deduct
  const [pct, setPct] = useState("");
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState([]);      // snapshot lines (edit) or live lines (create)
  const [subtotal, setSubtotal] = useState(0); // snapshot subtotal
  const [pendingRefresh, setPendingRefresh] = useState(null); // {est} awaiting confirm

  useEffect(() => {
    if (!open) return;
    setPendingRefresh(null);
    if (quote) {
      setName(quote.name || "");
      setDirection(quote.adjustment?.direction || "none");
      setPct(quote.adjustment?.pct != null ? String(quote.adjustment.pct) : "");
      setReason(quote.adjustment?.reason || "");
      setLines(quote.lines || []);
      setSubtotal(round2(quote.subtotal || 0));
    } else {
      setName(`${label} — Extra`);
      setDirection("none");
      setPct("");
      setReason("");
      setLines(snapLines(freshEst || { lines: [] }));
      setSubtotal(round2((freshEst || {}).total || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pctNum = Math.max(0, Math.min(direction === "deduct" ? 100 : 999, Number(pct) || 0));
  const t = useMemo(() => quoteTotals(subtotal, direction, pctNum), [subtotal, direction, pctNum]);

  const save = () => {
    const now = Date.now();
    onSave({
      id: quote?.id || uid(),
      name: name.trim() || `${label || quote?.label} — Extra`,
      label: quote?.label || label,
      createdAt: quote?.createdAt || now,
      updatedAt: now,
      lines,
      subtotal: t.subtotal,
      adjustment: { direction, pct: pctNum, reason: reason.trim() },
      adjustmentAmount: t.adjustmentAmount,
      total: t.total,
    });
    onClose();
  };

  const doRefresh = () => {
    const est = computeLive?.();
    if (est) setPendingRefresh({ est });
  };
  const confirmRefresh = () => {
    const est = pendingRefresh.est;
    setLines(snapLines(est));
    setSubtotal(round2(est.total || 0)); // adjustment re-derives from the NEW subtotal — never compounds
    setPendingRefresh(null);
  };

  const working = editing
    ? { name, label: quote.label, lines, subtotal: t.subtotal, adjustment: { direction, pct: pctNum, reason: reason.trim() }, adjustmentAmount: t.adjustmentAmount, total: t.total, updatedAt: quote.updatedAt, createdAt: quote.createdAt }
    : null;

  return (
    <Overlay open={open} onClose={onClose} wide>
      <DialogTitle onClose={onClose}>{editing ? "Edit extra / quote" : "Create extra / quote"}</DialogTitle>
      <div className="space-y-3" data-testid="quote-dialog">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label className={LABEL}>Quote name / reference</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT} data-testid="quote-name" />
          </div>
          <div>
            <label className={LABEL}>Source label</label>
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700" data-testid="quote-source-label">{quote?.label || label}</p>
          </div>
        </div>

        {/* scoped lines — every section that makes up the subtotal, supports included */}
        <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200" data-testid="quote-lines">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-slate-50">
              <tr className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">
                <th className="px-3 py-1.5">Description</th>
                <th className="px-2 py-1.5 text-right">Qty</th>
                <th className="px-2 py-1.5">Unit</th>
                <th className="px-2 py-1.5 text-right">Unit price</th>
                <th className="px-3 py-1.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {QUOTE_SECTIONS.map((sec) => {
                const rows = lines.filter((l) => l.section === sec);
                if (!rows.length) return null;
                const sub = round2(rows.reduce((s, l) => s + l.quantity * l.rate, 0));
                return [
                  <tr key={`${sec}-h`} className="bg-slate-100/70">
                    <td colSpan={4} className="px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-slate-500">{sec}{sec === "Rigid Supports" ? " — included" : ""}</td>
                    <td className={`px-3 py-1 text-right ${MONO} font-bold text-slate-700`}>{money(sub)}</td>
                  </tr>,
                  ...rows.map((l, i) => (
                    <tr key={`${sec}-${i}`} className="border-b border-slate-100">
                      <td className="px-3 py-1 text-slate-700">{l.description}</td>
                      <td className={`px-2 py-1 text-right ${MONO} text-slate-600`}>{fmtQty(l.quantity)}</td>
                      <td className={`px-2 py-1 ${MONO} text-slate-400`}>{l.unit}</td>
                      <td className={`px-2 py-1 text-right ${MONO} text-slate-600`}>{money(l.rate)}</td>
                      <td className={`px-3 py-1 text-right ${MONO} font-semibold text-slate-800`}>{money(round2(l.quantity * l.rate))}</td>
                    </tr>
                  )),
                ];
              })}
              {!lines.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No priced takeoffs carry this label.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* adjustment */}
        <div className="grid grid-cols-1 gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-3">
          <div>
            <label className={LABEL}>Adjustment</label>
            <select value={direction} onChange={(e) => setDirection(e.target.value)} className={INPUT} data-testid="quote-direction">
              <option value="none">No adjustment</option>
              <option value="add">Add %</option>
              <option value="deduct">Deduct %</option>
            </select>
          </div>
          <div>
            <label className={LABEL}>Percent</label>
            <input type="number" min="0" max={direction === "deduct" ? 100 : undefined} step="0.01" value={pct}
              onChange={(e) => setPct(e.target.value)} disabled={direction === "none"}
              className={`${INPUT} disabled:bg-slate-50`} data-testid="quote-pct" />
          </div>
          <div>
            <label className={LABEL}>Reason (optional)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={direction === "none"}
              placeholder="e.g. repeat-tenant pricing" className={`${INPUT} disabled:bg-slate-50`} data-testid="quote-reason" />
          </div>
        </div>

        {/* totals — adjustment rounded to cents once, applied to the cent subtotal */}
        <div className="rounded-lg bg-[#0b172a] p-3 text-white" data-testid="quote-totals">
          <div className="flex justify-between text-xs"><span className="text-white/60">Original subtotal</span><span className={MONO} data-testid="quote-subtotal">{money(t.subtotal)}</span></div>
          <div className="flex justify-between text-xs">
            <span className="text-white/60">{direction === "none" || !pctNum ? "Adjustment" : `${direction === "deduct" ? "Deduct" : "Add"} ${pctNum}%`}{reason ? ` — ${reason}` : ""}</span>
            <span className={`${MONO} ${t.adjustmentAmount < 0 ? "text-red-300" : "text-emerald-300"}`} data-testid="quote-adjustment">{t.adjustmentAmount ? money(t.adjustmentAmount) : "—"}</span>
          </div>
          <div className="mt-1 flex justify-between border-t border-white/10 pt-1 text-sm font-bold"><span>Quote total</span><span className={`${MONO} text-[#fb923c]`} data-testid="quote-total">{money(t.total)}</span></div>
        </div>

        {/* refresh from current takeoff (edit mode) — explicit, with before/after */}
        {editing && (
          <div className="rounded-lg border border-slate-200 p-3" data-testid="quote-refresh-box">
            {!pendingRefresh ? (
              <button className={BTN_GHOST} onClick={doRefresh} data-testid="quote-refresh">
                <RefreshCw className="mr-1 inline h-3.5 w-3.5" /> Refresh from current takeoff
              </button>
            ) : (
              <div className="space-y-2" data-testid="quote-refresh-preview">
                <p className="text-xs font-semibold text-slate-700">Replace the snapshot with today's takeoff for “{quote.label}”?</p>
                <p className={`${MONO} text-xs text-slate-600`}>
                  {lines.length} lines · {money(subtotal)} <span className="text-slate-400">→</span> {pendingRefresh.est.lines.length} lines · {money(round2(pendingRefresh.est.total || 0))}
                </p>
                <div className="flex gap-2">
                  <button className={BTN_ORANGE} onClick={confirmRefresh} data-testid="quote-refresh-confirm">Replace snapshot</button>
                  <button className={BTN_GHOST} onClick={() => setPendingRefresh(null)} data-testid="quote-refresh-cancel">Keep current</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          {editing && (
            <>
              <button className={BTN_GHOST} onClick={() => downloadQuoteCsv(working)} data-testid="quote-export-csv">Excel / CSV</button>
              <button className={BTN_GHOST} onClick={() => printQuote(working)} data-testid="quote-print"><Printer className="mr-1 inline h-3.5 w-3.5" /> Print</button>
            </>
          )}
          <button className={BTN_GHOST} onClick={onClose} data-testid="quote-cancel">Cancel</button>
          <button className={BTN_ORANGE} onClick={save} disabled={!lines.length} data-testid="quote-save">
            {editing ? "Save changes" : "Save quote"}
          </button>
        </div>
      </div>
    </Overlay>
  );
}
